import { useMemo } from "react";
import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FetcherTablePagination from "../FetcherTablePagination";
import { SortDirection, sortRows } from "../helpers/tableSorting";

export interface DevObjectTableRow {
  id: string;
  objectType: string;
  objectValue: string;
  fieldType: string;
  fieldValue: string;
  /** false for a malformed level - the collector never matches it */
  isReachable: boolean;
}

interface Props {
  rows: DevObjectTableRow[];
  sortKey: string;
  sortDirection: SortDirection;
  onSort: (key: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  canEdit: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const headCells: { key: string; label: string; sortable: boolean }[] = [
  { key: "id", label: "ID", sortable: true },
  { key: "objectType", label: "object_type", sortable: true },
  { key: "objectValue", label: "object_value", sortable: true },
  { key: "fieldType", label: "field_type", sortable: true },
  { key: "fieldValue", label: "field_value", sortable: false },
  { key: "actions", label: "", sortable: false },
];

const DevObjectsTable = ({
  rows,
  sortKey,
  sortDirection,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  canEdit,
  onEdit,
  onDelete,
}: Props) => {
  const sorted = useMemo(
    () => sortRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection],
  );
  const pageRows = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.key}
                  align={cell.key === "actions" ? "right" : "left"}
                >
                  {cell.sortable ? (
                    <TableSortLabel
                      active={sortKey === cell.key}
                      direction={sortKey === cell.key ? sortDirection : "asc"}
                      onClick={() => onSort(cell.key)}
                    >
                      {cell.label}
                    </TableSortLabel>
                  ) : (
                    cell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "#00000099", py: 3 }}
                  >
                    No rows match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell className="fetcher-mono">{row.id}</TableCell>
                <TableCell>
                  {row.isReachable ? (
                    <Typography
                      className="fetcher-mono"
                      sx={{ fontSize: "12px" }}
                    >
                      {row.objectType}
                    </Typography>
                  ) : (
                    <Chip size="small" color="error" label={row.objectType} />
                  )}
                </TableCell>
                <TableCell className="fetcher-mono">
                  {row.objectValue}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: "12.5px", fontWeight: 500 }}>
                    {row.fieldType}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    className="fetcher-mono"
                    sx={{ fontSize: "12px", wordBreak: "break-all" }}
                  >
                    {row.fieldValue}
                  </Typography>
                  {!row.isReachable && (
                    <Typography sx={{ fontSize: "11.5px", color: "#C0392B" }}>
                      Unreachable — the collector matches on the four standard
                      levels only, so this row is never applied.
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Stack direction="row" justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row.id)}
                          disabled
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(row.id)}
                          disabled
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FetcherTablePagination
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
};

export default DevObjectsTable;
